<template>
  <component :is="tag" class="ds-text-split" :class="`ds-text-split--${as}`">
    <span class="ds-text-split__base" :style="baseStyle">{{ base }}</span>
    <span class="ds-text-split__highlight-group">
      <span class="ds-text-split__highlight" :style="highlightStyle">{{ highlight }}</span>
      <span
        v-if="suffix"
        class="ds-text-split__suffix"
        :style="suffixStyle"
      >{{ suffix }}</span>
    </span>
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'

type Variant =
  | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'h7' | 'h8'
  | 'subtitle1' | 'subtitle2' | 'subtitle3' | 'subtitle4' | 'subtitle5'
  | 'subtitle6' | 'subtitle7' | 'subtitle8' | 'subtitle9'
  | 'subtitle10' | 'subtitle11' | 'subtitle12' | 'subtitle13' | 'subtitle14' | 'subtitle15'
  | 'body1' | 'body2' | 'body3' | 'body4'
  | 'bebas1' | 'bebas2' | 'bebas3' | 'bebas4' | 'bebas5' | 'bebas6'
  | 'button'

const tagMap: Record<string, string> = {
  h1: 'h1', h2: 'h2', h3: 'h3', h4: 'h4', h5: 'h5', h6: 'h6',
  h7: 'p', h8: 'p',
  subtitle1: 'p', subtitle2: 'p', subtitle3: 'p', subtitle4: 'p',
  subtitle5: 'p', subtitle6: 'p', subtitle7: 'p', subtitle8: 'p',
  subtitle9: 'p', subtitle10: 'p', subtitle11: 'p', subtitle12: 'p',
  subtitle13: 'p', subtitle14: 'p', subtitle15: 'p',
  body1: 'p', body2: 'p', body3: 'p', body4: 'p',
  bebas1: 'p', bebas2: 'p', bebas3: 'p', bebas4: 'p', bebas5: 'p', bebas6: 'p',
  button: 'span'
}

const props = withDefaults(defineProps<{
  as: Variant
  base: string
  highlight: string
  suffix?: string
  baseColor?: string
  highlightColor?: string
  suffixColor?: string
}>(), {
  suffix: undefined,
  baseColor: undefined,
  highlightColor: undefined,
  suffixColor: undefined,
})

const tag = computed(() => tagMap[props.as] ?? 'p')
const baseStyle = computed(() => props.baseColor ? { color: props.baseColor } : {})
const highlightStyle = computed(() => props.highlightColor ? { color: props.highlightColor } : {})
const suffixStyle = computed(() => {
  const color = props.suffixColor ?? props.baseColor

  return color ? { color } : {}
})
</script>

<style scoped>
.ds-text-split {
  display: inline-flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0 8px;
  margin: 0;
}

.ds-text-split__base,
.ds-text-split__highlight,
.ds-text-split__suffix {
  display: inline;
}

.ds-text-split__highlight-group {
  display: inline-flex;
}

.ds-text-split--h1,
.ds-text-split--h2,
.ds-text-split--h3,
.ds-text-split--h4,
.ds-text-split--h5,
.ds-text-split--h6,
.ds-text-split--h7,
.ds-text-split--h8 {
  font-family: var(--ds-font-family-bebas);
}

.ds-text-split--h1 { font-size: var(--ds-h1-size); font-weight: var(--ds-h1-weight); }
.ds-text-split--h2 { font-size: var(--ds-h2-size); font-weight: var(--ds-h2-weight); }
.ds-text-split--h3 { font-size: var(--ds-h3-size); font-weight: var(--ds-h3-weight); }
.ds-text-split--h4 { font-size: var(--ds-h4-size); font-weight: var(--ds-h4-weight); }
.ds-text-split--h5 { font-size: var(--ds-h5-size); font-weight: var(--ds-h5-weight); }
.ds-text-split--h6 { font-size: var(--ds-h6-size); font-weight: var(--ds-h6-weight); }
.ds-text-split--h7 { font-size: var(--ds-h7-size); font-weight: var(--ds-h7-weight); }
.ds-text-split--h8 { font-size: var(--ds-h8-size); font-weight: var(--ds-h8-weight); }

.ds-text-split--subtitle1  { font-size: var(--ds-subtitle1-size);  font-weight: var(--ds-subtitle1-weight);  font-family: var(--ds-font-family-base); }
.ds-text-split--subtitle2  { font-size: var(--ds-subtitle2-size);  font-weight: var(--ds-subtitle2-weight);  font-family: var(--ds-font-family-base); }
.ds-text-split--subtitle3  { font-size: var(--ds-subtitle3-size);  font-weight: var(--ds-subtitle3-weight);  font-family: var(--ds-font-family-base); }
.ds-text-split--subtitle4  { font-size: var(--ds-subtitle4-size);  font-weight: var(--ds-subtitle4-weight);  font-family: var(--ds-font-family-base); }
.ds-text-split--subtitle5  { font-size: var(--ds-subtitle5-size);  font-weight: var(--ds-subtitle5-weight);  font-family: var(--ds-font-family-base); }
.ds-text-split--subtitle6  { font-size: var(--ds-subtitle6-size);  font-weight: var(--ds-subtitle6-weight);  font-family: var(--ds-font-family-base); }
.ds-text-split--subtitle7  { font-size: var(--ds-subtitle7-size);  font-weight: var(--ds-subtitle7-weight);  font-family: var(--ds-font-family-base); }
.ds-text-split--subtitle8  { font-size: var(--ds-subtitle8-size);  font-weight: var(--ds-subtitle8-weight);  font-family: var(--ds-font-family-base); }
.ds-text-split--subtitle9  { font-size: var(--ds-subtitle9-size);  font-weight: var(--ds-subtitle9-weight);  font-family: var(--ds-font-family-base); }
.ds-text-split--subtitle10 { font-size: var(--ds-subtitle10-size); font-weight: var(--ds-subtitle10-weight); font-family: var(--ds-font-family-base); }
.ds-text-split--subtitle11 { font-size: var(--ds-subtitle11-size); font-weight: var(--ds-subtitle11-weight); font-family: var(--ds-font-family-base); }
.ds-text-split--subtitle12 { font-size: var(--ds-subtitle12-size); font-weight: var(--ds-subtitle12-weight); font-family: var(--ds-font-family-base); }
.ds-text-split--subtitle13 { font-size: var(--ds-subtitle13-size); font-weight: var(--ds-subtitle13-weight); font-family: var(--ds-font-family-base); }
.ds-text-split--subtitle14 { font-size: var(--ds-subtitle14-size); font-weight: var(--ds-subtitle14-weight); font-family: var(--ds-font-family-base); }
.ds-text-split--subtitle15 { font-size: var(--ds-subtitle15-size); font-weight: var(--ds-subtitle15-weight); font-family: var(--ds-font-family-base); }

.ds-text-split--body1 { font-size: var(--ds-body1-size); font-weight: var(--ds-body1-weight); font-family: var(--ds-font-family-base); }
.ds-text-split--body2 { font-size: var(--ds-body2-size); font-weight: var(--ds-body2-weight); font-family: var(--ds-font-family-base); }
.ds-text-split--body3 { font-size: var(--ds-body3-size); font-weight: var(--ds-body3-weight); font-family: var(--ds-font-family-base); }
.ds-text-split--body4 { font-size: var(--ds-body4-size); font-weight: var(--ds-body4-weight); font-family: var(--ds-font-family-base); }

.ds-text-split--bebas1 { font-size: var(--ds-bebas1-size); font-weight: var(--ds-bebas1-weight); font-family: var(--ds-font-family-bebas); }
.ds-text-split--bebas2 { font-size: var(--ds-bebas2-size); font-weight: var(--ds-bebas2-weight); font-family: var(--ds-font-family-bebas); }
.ds-text-split--bebas3 { font-size: var(--ds-bebas3-size); font-weight: var(--ds-bebas3-weight); font-family: var(--ds-font-family-bebas); }
.ds-text-split--bebas4 { font-size: var(--ds-bebas4-size); font-weight: var(--ds-bebas4-weight); font-family: var(--ds-font-family-bebas); }
.ds-text-split--bebas5 { font-size: var(--ds-bebas5-size); font-weight: var(--ds-bebas5-weight); font-family: var(--ds-font-family-bebas); }
.ds-text-split--bebas6 { font-size: var(--ds-bebas6-size); font-weight: var(--ds-bebas6-weight); font-family: var(--ds-font-family-bebas); }

.ds-text-split--button { font-size: var(--ds-button-size); font-weight: var(--ds-button-weight); font-family: var(--ds-font-family-base); }
</style>
